import type { IGpthelpToolGate } from './tool-types';

/*
 * The store settings read's sections and who may read each one (plan §8
 * R1). A leaf module: it imports only the gate type, so the settings tool
 * and the host context (its `_semantics.settings`) can ask which sections a
 * member reads without loading the tool modules.
 */

/** The settings sections, in the order the description lists them. */
export const STORE_SETTINGS_SECTIONS = [
  'ordering',
  'hours',
  'delivery',
  'printing',
  'branches',
  'team',
] as const;
export type TStoreSettingsSection = typeof STORE_SETTINGS_SECTIONS[number];

/** The settings page's «Pedidos» tab (order types, payments, delivery) needs Plus. */
const ORDERS_TAB_MIN_TIER = 1;
/** The settings page's «Información adicional» tab (hours) is on every plan. */
const INFO_TAB_MIN_TIER = 0;
/** The team page (`users-staff`) needs Plus. */
const TEAM_MIN_TIER = 1;
/** The «Sucursales» page (`store-branches`), which shows every branch's address, needs Pro. */
const BRANCH_PAGE_MIN_TIER = 2;

/**
 * The «Sucursales» page (BranchSettings, Pro): each branch the member works
 * in with its own settings tabs, address included (App.ts:495-499 opens
 * BranchSettings.vue, the settings page with `only-branch`).
 */
export function opensBranchesPage(gate: IGpthelpToolGate): boolean {
  return gate.canManageBranches && gate.effectiveTier >= BRANCH_PAGE_MIN_TIER;
}

/**
 * Whether the member sees a branch tab (ordering and delivery: «Pedidos»;
 * hours: «Información adicional») on a page: on the store settings page
 * (StoreSettings) unless the store edits its branches separately, where
 * that page only links to «Sucursales» (RestaurantSettings.vue:2438-2443
 * `multipleBranches`; the tabs at :563 and :836), or on «Sucursales».
 */
function seesBranchTab(gate: IGpthelpToolGate, minTier: number): boolean {
  const onSettingsPage =
    gate.canManageStore &&
    !gate.settingsPerBranch &&
    gate.effectiveTier >= minTier;
  return onSettingsPage || opensBranchesPage(gate);
}

/** Who may read each section: the gate of the page that shows it (plan §8 R1). */
const SECTION_GATES: Readonly<
  Record<TStoreSettingsSection, (gate: IGpthelpToolGate) => boolean>
> = {
  ordering: (gate) => seesBranchTab(gate, ORDERS_TAB_MIN_TIER),
  hours: (gate) => seesBranchTab(gate, INFO_TAB_MIN_TIER),
  delivery: (gate) => seesBranchTab(gate, ORDERS_TAB_MIN_TIER),
  // The «Impresión» section of the Menú's categories and products.
  printing: (gate) => gate.canManageCatalog,
  // The branches the member works in: every member of the store sees them.
  branches: () => true,
  team: (gate) => gate.canManageUsers && gate.effectiveTier >= TEAM_MIN_TIER,
};

/** The sections this member may read, in the description's order. */
export function storeSettingsSections(
  gate: IGpthelpToolGate
): TStoreSettingsSection[] {
  if (!gate.hasRestaurantScope) return [];
  return STORE_SETTINGS_SECTIONS.filter((section) =>
    SECTION_GATES[section](gate)
  );
}
